import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { useMutation, useQuery, useQueryClient, QueryClient } from "@tanstack/react-query";
import { useAuthContext, showGlobalError } from "../contexts";

const TIMEOUT = Number.parseInt(process.env.REACT_APP_TIMEOUT_MS || '10000');
const BASE_URL = process.env.REACT_APP_SERVER_URL;

// Marks requests made by the authenticated hooks below. The QueryCache/MutationCache onError in App.tsx
// runs handleApiError once per failed request for these only (login handles its own 401).
const API_ERROR_META = { handleApiErrors: true };

export function handleApiErrorFor(error: unknown, meta: Record<string, unknown> | undefined): void {
  if (meta?.handleApiErrors) handleApiError(error);
}

// Centralized error handler
function handleApiError(error: unknown): void {
  if (!error) return;

  const axiosError = error as AxiosError;
  const status = axiosError?.response?.status;

  if (status === 401) {
    localStorage.removeItem('portal_rs_auth');
    window.location.reload();
    return;
  }

  // Show toast for communication errors (network errors, timeouts, server errors)
  if (axiosError?.code === 'ECONNABORTED') {
    showGlobalError('Connection Timeout', 'The server took too long to respond. Please try again.');
  } else if (axiosError?.code === 'ERR_NETWORK' || !axiosError?.response) {
    showGlobalError('Connection Error', 'Unable to connect to the server. Please check your network connection.');
  } else if (status && status >= 500) {
    showGlobalError('Server Error', 'An unexpected server error occurred. Please try again later.');
  }
}

// Hook to get auth headers
export function useAuthHeaders() {
  const { authInfo } = useAuthContext();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  if (authInfo.token) {
    headers['Authorization'] = `Bearer ${authInfo.token}`;
  }

  return { headers, token: authInfo.token };
}

// Simplified authenticated query hook (React Query v4 format)
export function useAuthQuery(
  queryKey: unknown[],
  endpoint: string,
  options?: { enabled?: boolean; cacheTime?: number; staleTime?: number }
) {
  const { headers, token } = useAuthHeaders();

  const query = useQuery(
    queryKey,
    () => axios.get(`${BASE_URL}/${endpoint}`, { headers, timeout: TIMEOUT }),
    {
      enabled: token !== null && (options?.enabled ?? true),
      cacheTime: options?.cacheTime ?? 10 * 60 * 1000,
      staleTime: options?.staleTime ?? 0,
      meta: API_ERROR_META,
    }
  );

  return query;
}

// Simplified authenticated POST query hook (React Query v4 format)
export function useAuthPostQuery<D = unknown>(
  queryKey: unknown[],
  endpoint: string,
  data: D,
  options?: { enabled?: boolean; cacheTime?: number }
) {
  const { headers, token } = useAuthHeaders();

  const query = useQuery(
    queryKey,
    () => axios.post(`${BASE_URL}/${endpoint}`, data, { headers, timeout: TIMEOUT }),
    {
      enabled: token !== null && (options?.enabled ?? true),
      cacheTime: options?.cacheTime ?? 10 * 60 * 1000,
      meta: API_ERROR_META,
    }
  );

  return query;
}

// Simplified authenticated mutation hook (React Query v4 format)
// Returns full axios response for backward compatibility
export function useAuthMutation<T = unknown, V = unknown>(
  mutationKey: string[],
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  getEndpoint: (variables: V) => string,
  options?: {
    invalidateKeys?: string[][];
    getData?: (variables: V) => unknown;
    getParams?: (variables: V) => unknown;
  }
) {
  const queryClient = useQueryClient();
  const { headers } = useAuthHeaders();

  const mutation = useMutation(
    async (variables: V): Promise<T> => {
      const config: AxiosRequestConfig = {
        method,
        url: `${BASE_URL}/${getEndpoint(variables)}`,
        headers,
        timeout: TIMEOUT,
        data: options?.getData?.(variables),
        params: options?.getParams?.(variables),
      };

      const response = await axios(config);

      if (options?.invalidateKeys) {
        options.invalidateKeys.forEach(key => queryClient.invalidateQueries(key));
      }

      // Return full response for backward compatibility
      return response as unknown as T;
    },
    { mutationKey, meta: API_ERROR_META }
  );

  return mutation;
}

export function invalidateDocumentList(queryClient: QueryClient) {
  queryClient.invalidateQueries(['GetDocumentList']);
}
